/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-param-reassign */
/* eslint-disable no-lonely-if */
import { action, Action, computed, Computed, thunk, Thunk, ThunkOn, thunkOn } from 'easy-peasy';
import { AxiosResponse } from 'axios';
import isObject from 'lodash/isObject';
import {
  BusinessLocationsResponse,
  FetchRecruiterResponseT,
  FollowStateEnum,
  Location,
  LocationFilterPayload,
  LocationFilterQuery,
  Position,
  PositionAddressResponse,
  PositionFilterPayload,
  PositionFilters,
  PositionResponse,
  PositionSort,
  Recruiter,
  RecruiterFilterPayload,
  RecruiterFilters,
} from '../types';
import { HttpStoreModel } from '../../../../../http/models/httpModel';
import HttpClientWithRetry from '../../../../../http/client/HttpClientWithRetry';
import { MonitorByRecruitersController } from './monitorByRecruitersController';

const MAX_SELECTION_CAP = 10000000;

export interface MonitorByRecruitersModel {
  // --- Recruiters ---
  recruiters: Recruiter[];
  selectedRecruiters: string[];
  isRecruitersLoading: boolean;
  isRecruitersFetching: boolean;

  isBulkAssignMode: boolean;
  isAllPositionsSelected: boolean;
  selectedPositions: {
    positionId: string;
    businessId: string;
  }[];
  // selectedBusinessPositions: Computed<
  //   MonitorByRecruitersModel,
  //   {
  //     positionId: string;
  //     businessId: string;
  //   }[]
  // >;

  // --- Positions ---
  isPositionfilterActive: boolean;
  positions: Position[];
  selectedPosition: Position | null;
  isFetchingBusinessAddress: boolean;
  businessAddress: PositionAddressResponse['result']['provinceCities'];
  districts: PositionAddressResponse['result']['districts'] | null;
  isFetchingPosition: boolean;
  statusUpdatingRecruiterId: null | string;
  positionResponse: null | PositionResponse;

  // --- Locations ---
  isLocationsfetching: boolean;
  locations: Location[];
  locationQuery: LocationFilterQuery;
  // --- Filters ---
  positionFilters: PositionFilters;
  recruiterFilters: RecruiterFilters;
  isBulkAssignInProgress: boolean;
  hasUserInteractedWithPositions: boolean;

  // --- Derived ---
  filteredRecruiters: Computed<MonitorByRecruitersModel, Recruiter[]>;
  areAllRecruitersSelected: Computed<MonitorByRecruitersModel, boolean>;
  isPositionSelected: Computed<MonitorByRecruitersModel, (id: string) => boolean>;
  areAllPositionsSelected: Computed<MonitorByRecruitersModel, boolean>;
  isSomePositionsSelected: Computed<MonitorByRecruitersModel, boolean>;
  computedPositionFilters: Computed<MonitorByRecruitersModel, any>;
  onPositionsLoaded: ThunkOn<MonitorByRecruitersModel, Position[]>;

  // --- Actions ---
  setRecruiters: Action<MonitorByRecruitersModel, Recruiter[]>;
  setSelectedRecruiters: Action<MonitorByRecruitersModel, string[]>;
  setPositions: Action<MonitorByRecruitersModel, Position[]>;
  setSelectedPosition: Action<MonitorByRecruitersModel, Position | null>;
  setPositionFilter: Action<MonitorByRecruitersModel, PositionFilterPayload>;
  resetPositionFilters: Action<MonitorByRecruitersModel>;
  setRecruiterFilter: Action<MonitorByRecruitersModel, RecruiterFilterPayload>;
  resetRecruiterFilters: Action<MonitorByRecruitersModel>;
  setPositionLoader: Action<MonitorByRecruitersModel, boolean>;
  setPositionFetch: Action<MonitorByRecruitersModel, boolean>;
  setPositionfilterActive: Action<MonitorByRecruitersModel, boolean>;
  setIsFetchingBusinessAddress: Action<MonitorByRecruitersModel, boolean>;
  setBusinessAddress: Action<MonitorByRecruitersModel, PositionAddressResponse['result']['provinceCities']>;
  setDistricts: Action<MonitorByRecruitersModel, PositionAddressResponse['result']['districts']>;
  setIsFetchingPosition: Action<MonitorByRecruitersModel, boolean>;
  setLocationFetching: Action<MonitorByRecruitersModel, boolean>;
  setLocations: Action<MonitorByRecruitersModel, Location[]>;
  setLocationQuery: Action<MonitorByRecruitersModel, LocationFilterPayload>;
  setStatusUpdatingRecruiterId: Action<MonitorByRecruitersModel, string | null>;
  setPositionResponse: Action<MonitorByRecruitersModel, PositionResponse | null>;
  resetSelectedPositions: Action<MonitorByRecruitersModel>;
  setCurrentPagePositions: Action<MonitorByRecruitersModel>;
  setHasUserInteractedWithPositions: Action<MonitorByRecruitersModel, boolean>;

  setBulkAssignMode: Action<MonitorByRecruitersModel, boolean>;
  setIsAllPositionsSelected: Action<MonitorByRecruitersModel, boolean>;
  setBulkAssignInProgress: Action<MonitorByRecruitersModel, boolean>;
  setSelectedPositions: Action<MonitorByRecruitersModel, { positions: string[]; isAllSelected: boolean; isDirty?: boolean }>;
  setSelectAPosition: Action<
    MonitorByRecruitersModel,
    { positionId: string; businessId: string; onError?: (message: string) => void }
  >;
  //   assignStatusOptimistic: Action<MonitorByRecruitersModel, { positionIds: string[]; newStatus: PositionStatus }>;

  // --- Thunks ---
  fetchRecruiters: Thunk<
    MonitorByRecruitersModel,
    { accountId: string },
    void,
    HttpStoreModel,
    Promise<AxiosResponse<FetchRecruiterResponseT>>
  >;
  fetchRecruitersWithOperator: Thunk<
    MonitorByRecruitersModel,
    { accountId: string },
    void,
    HttpStoreModel,
    Promise<AxiosResponse<FetchRecruiterResponseT>>
  >;
  fetchBusinessAddress: Thunk<MonitorByRecruitersModel, undefined, void, HttpStoreModel, Promise<PositionAddressResponse>>;
  fetchPositions: Thunk<
    MonitorByRecruitersModel,
    { accountId: string; fromApplyFilter?: boolean },
    void,
    HttpStoreModel,
    Promise<AxiosResponse<PositionResponse>>
  >;
  fetchLocations: Thunk<
    MonitorByRecruitersModel,
    undefined,
    void,
    HttpStoreModel,
    Promise<AxiosResponse<BusinessLocationsResponse>>
  >;
  fetchJobs: Thunk<
    MonitorByRecruitersModel,
    { accountId: string },
    void,
    HttpStoreModel,
    Promise<Record<string, 'open' | 'closed' | 'archived'>>
  >;
  updatePositionStatusOfRecruiter: Thunk<
    MonitorByRecruitersModel,
    { positionId: string; recruiterId: string; status: FollowStateEnum },
    void,
    HttpStoreModel,
    Promise<AxiosResponse>
  >;
  bulkUpdate: Thunk<
    MonitorByRecruitersModel,
    { accountId: string; followedStatus: FollowStateEnum },
    void,
    HttpStoreModel,
    Promise<AxiosResponse>
  >;
}

export type MonitorByRecruitersStoreModel = {
  moniterByRecruiters: MonitorByRecruitersModel;
};

export const defaultPositionFilter: PositionFilters = {
  isAllLocationsSelected: true,
  positionStatus: ['open', 'closed', 'archived'],
  isAllJobSelected: true,
  isAllProvinceSelected: true,
  isAllCitySelected: true,
  isAllDistrictSelected: true,
  sortBy: PositionSort.Location,
  limit: 40,
  page: 1,
};

export const monitorByRecruitersModel: MonitorByRecruitersModel = {
  recruiters: [],
  selectedRecruiters: [],

  positions: [],
  selectedPosition: null,

  isLocationsfetching: false,
  locations: [],
  locationQuery: {
    page: 1,
    limit: 10000,
    sortBy: 'name',
    sortOrder: 'asc',
  },

  isBulkAssignMode: false,
  isAllPositionsSelected: false,
  selectedPositions: [],
  // selectedBusinessPositions: [],

  positionFilters: defaultPositionFilter,
  recruiterFilters: { search: '' },
  isRecruitersLoading: false,
  isRecruitersFetching: false,
  isPositionfilterActive: false,
  businessAddress: {},
  districts: {},
  isFetchingPosition: false,
  statusUpdatingRecruiterId: null,
  positionResponse: null,
  isBulkAssignInProgress: false,
  hasUserInteractedWithPositions: false,

  // --- Derived ---
  filteredRecruiters: computed((state) => {
    const term = state.recruiterFilters.search?.trim().toLowerCase();
    const filtered = state.recruiters.filter(
      (r) => r.firstName?.trim().toLowerCase().includes(term) || r.lastName?.trim().toLowerCase().includes(term),
    );

    return filtered.sort((a, b) => {
      const firstCompare = a.firstName?.trim().localeCompare(b.firstName?.trim());
      if (firstCompare !== 0) return firstCompare;
      return a.lastName?.trim().localeCompare(b.lastName?.trim());
    });
  }),

  areAllRecruitersSelected: computed((state) => {
    const { recruiters, selectedRecruiters } = state;
    if (recruiters.every((r) => selectedRecruiters.includes(r.id)) || selectedRecruiters.length === 0) return true;

    return false;
  }),
  isPositionSelected: computed((state) => (id: string) =>
    state.isAllPositionsSelected || state.selectedPositions.some((p) => p.positionId === id),
  ),
  areAllPositionsSelected: computed((state) => {
    if (state.isAllPositionsSelected) return true;
    return state.positions.every((p) => {
      return state.selectedPositions.some((sp) => sp.positionId === p.id);
    });
  }),

  isSomePositionsSelected: computed((state) => {
    if (state.isAllPositionsSelected) return false;
    return !state.areAllPositionsSelected && state.selectedPositions.length > 0;
  }),

  computedPositionFilters: computed(
    [
      (state) => state.positionFilters,
      (state) => state.selectedRecruiters,
      (state) => state.areAllRecruitersSelected,
      (state) => state.districts,
    ],
    (positionFilters, selectedRecruiters, areAllRecruitersSelected, districts) => {
      const allSelectedMap: Record<string, string> = {
        isAllLocationsSelected: 'businessIds',
        isAllJobSelected: 'jobCustomNames',
        isAllProvinceSelected: 'province',
        isAllCitySelected: 'city',
        isAllDistrictSelected: 'district',
      };

      // 1. Create a shallow copy of positionFilters to mutate safely
      const newFilters: Record<string, any> = { ...positionFilters };

      if (!areAllRecruitersSelected) {
        newFilters.recruiters = selectedRecruiters;
      }

      Object.entries(allSelectedMap).forEach(([flag, key]) => {
        if (newFilters[flag]) {
          delete newFilters[key];
        }
        delete newFilters[flag];
      });

      if (positionFilters.sortBy === PositionSort.Location) {
        newFilters.sortBy = 'businessName.keyword';
      }
      if (positionFilters.sortBy === PositionSort.Followed) {
        newFilters.sortBy = 'assignedRecruitersCount';
      }
      if (positionFilters.sortBy === PositionSort.Watched) {
        newFilters.sortBy = 'watchedRecruitersCount';
      }
      /* istanbul ignore next */
      if (
        isObject(districts) &&
        /* istanbul ignore next */ Array.isArray(newFilters.district) &&
        newFilters.district.length > 0
      ) {
        const selectedDistrictBusinessIds: string[] = Array.from(
          new Set(newFilters.district.flatMap((key: any) => districts[key] ?? [])),
        );

        const existingBusinessIds = Array.isArray(newFilters.businessIds) ? newFilters.businessIds : [];

        newFilters.businessIds = Array.from(new Set([...existingBusinessIds, ...selectedDistrictBusinessIds]));

        delete newFilters.district;
      }

      return newFilters;
    },
  ),

  onPositionsLoaded: thunkOn(
    (actions) => actions.setPositions,
    (actions, _, helpers) => {
      const state = helpers.getState();
      const { isBulkAssignMode } = state;

      if (!isBulkAssignMode) {
        actions.resetSelectedPositions();
      }
    },
  ),

  // --- Actions ---
  setRecruiters: action((state, recruiters) => {
    state.recruiters = recruiters;
  }),
  setSelectedRecruiters: action((state, recruiters) => {
    state.selectedRecruiters = Array.from(new Set(recruiters));
  }),

  setPositions: action((state, positions) => {
    state.positions = positions;
  }),
  setSelectedPosition: action((state, positionId) => {
    // const idx = state.selectedPositions.indexOf(positionId);
    // if (idx >= 0) state.selectedPositions.splice(idx, 1);
    // else state.selectedPositions.push(positionId);
    state.selectedPosition = positionId;
  }),

  setPositionFilter: action((state, { key, value }) => {
    state.positionFilters[key] = value as never;
  }),
  resetPositionFilters: action((state) => {
    state.positionFilters = defaultPositionFilter;

    state.isBulkAssignMode = false;
    state.isAllPositionsSelected = false;
    state.selectedPositions = [];
  }),

  setRecruiterFilter: action((state, { key, value }) => {
    state.recruiterFilters[key] = value as never;
  }),
  resetRecruiterFilters: action((state) => {
    state.recruiterFilters = {
      search: '',
    };
  }),
  setHasUserInteractedWithPositions: action((state, value) => {
    state.hasUserInteractedWithPositions = value;
  }),

  setPositionLoader: action((state, value) => {
    state.isRecruitersLoading = value;
  }),
  setPositionFetch: action((state, value) => {
    state.isRecruitersFetching = value;
  }),
  setPositionfilterActive: action((state, value) => {
    state.isPositionfilterActive = value;
  }),
  setIsFetchingBusinessAddress: action((state, value) => {
    state.isFetchingBusinessAddress = value;
  }),
  setBusinessAddress: action((state, value) => {
    state.businessAddress = value;
  }),
  setDistricts: action((state, value) => {
    state.districts = value;
  }),
  setIsFetchingPosition: action((state, value) => {
    state.isFetchingPosition = value;
  }),
  setLocationFetching: action((state, value) => {
    state.isLocationsfetching = value;
  }),
  setLocationQuery: action((state, { key, value }) => {
    state.locationQuery[key] = value as never;
  }),
  setLocations: action((state, value) => {
    state.locations = value;
  }),
  setStatusUpdatingRecruiterId: action((state, value) => {
    state.statusUpdatingRecruiterId = value;
  }),
  setPositionResponse: action((state, value) => {
    state.positionResponse = value;
  }),
  setBulkAssignInProgress: action((state, value) => {
    state.isBulkAssignInProgress = value;
  }),

  setBulkAssignMode: action((state, value) => {
    state.isBulkAssignMode = value;
    state.selectedPositions = [];
    state.isAllPositionsSelected = false;
    state.selectedPosition = null;
  }),

  setIsAllPositionsSelected: action((state, value) => {
    if (value) {
      state.selectedPositions = [];
    }
    state.isAllPositionsSelected = value;
  }),
  setSelectedPositions: action((state, { isAllSelected, isDirty = true }) => {
    const currentPageIds = state.positions?.map((p) => p.id) ?? /* istanbul ignore next */ [];
    const currentPageBusinessPositionIds =
      state.positions?.map((p) => ({ positionId: p.id, businessId: p.business })) ?? /* istanbul ignore next */ [];

    state.hasUserInteractedWithPositions = isDirty;
    if (isAllSelected) {
      /* istanbul ignore next */
      const currentSelectionSet = new Set(state.selectedPositions?.map((item) => item.positionId));

      const newIdsToSelect = currentPageIds.filter((id) => !currentSelectionSet.has(id));

      const availableSlots = MAX_SELECTION_CAP - state.selectedPositions.length;

      const countToSelect = Math.min(newIdsToSelect.length, availableSlots);

      if (countToSelect > 0) {
        // eslint-disable-next-line
        for (let i = 0; i < countToSelect; i++) {
          currentSelectionSet.add(newIdsToSelect[i]);
        }
      }

      const mergeUniqueByPositionId = (
        a: { positionId: string; businessId: string }[],
        b: { positionId: string; businessId: string }[],
      ) => {
        const map = new Map<string, { positionId: string; businessId: string }>();

        [...a, ...b].forEach((item) => {
          map.set(item.positionId, item); // b overwrites a if duplicate
        });

        return Array.from(map.values());
      };

      state.selectedPositions = mergeUniqueByPositionId(currentPageBusinessPositionIds, state.selectedPositions)?.filter((p) =>
        currentSelectionSet.has(p.positionId),
      );

      return;
    }

    const toRemove = new Set(currentPageIds);
    /* istanbul ignore next */
    state.selectedPositions = state.selectedPositions.filter((id) => !toRemove.has(id.positionId));
  }),
  resetSelectedPositions: action((state) => {
    state.selectedPositions = [];
    state.isAllPositionsSelected = false;
    state.hasUserInteractedWithPositions = false;
  }),

  setCurrentPagePositions: action(
    /* istanbul ignore next */ (state) => {
      const selectedPositions = state.positions?.map((p) => ({ positionId: p.id, businessId: p.business }));
      state.selectedPositions = selectedPositions;
    },
  ),
  setSelectAPosition: action((state, { positionId, businessId, onError }) => {
    const isSelected = state.isPositionSelected(positionId);
    const { isAllPositionsSelected } = state;

    state.hasUserInteractedWithPositions = true;

    state.isAllPositionsSelected = false;
    let positions = [...state.selectedPositions];

    // istanbul ignore next
    if (isAllPositionsSelected) {
      positions = state.positions
        ?.map((p) => ({ positionId: p.id, businessId: p.business }))
        ?.filter((p) => p.positionId !== positionId);
    } else if (isSelected) {
      positions = positions?.filter((p) => p.positionId !== positionId);
    } else {
      /* istanbul ignore else */
      if (positions.length < MAX_SELECTION_CAP) {
        positions = [...positions, { positionId, businessId }];
      } else {
        onError?.(`Cannot select more than ${MAX_SELECTION_CAP} positions.`);
        return;
      }
    }
    state.selectedPositions = positions;
  }),

  // --- Thunks ---
  fetchRecruiters: thunk(async (actions, { accountId }, { getState, getStoreState }) => {
    const { recruiters } = getState();

    const hasRecruiter = recruiters.length > 0;
    actions.setPositionFetch(hasRecruiter);
    actions.setPositionLoader(!hasRecruiter);

    const controller = new MonitorByRecruitersController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.fetchRecruiters(accountId).finally(() => {
      actions.setPositionFetch(false);
      actions.setPositionLoader(false);
    });
    const responseData = response.data?.data ?? [];

    actions.setRecruiters(responseData);
    actions.setSelectedRecruiters([...responseData?.map((e) => e.id)]);
    return response;
  }),

  fetchRecruitersWithOperator: thunk(async (actions, { accountId }, { getState, getStoreState }) => {
    const { recruiters } = getState();

    const hasRecruiter = recruiters.length > 0;
    actions.setPositionFetch(hasRecruiter);
    actions.setPositionLoader(!hasRecruiter);

    const controller = new MonitorByRecruitersController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.fetchRecruitersWithOperator(accountId).finally(() => {
      actions.setPositionFetch(false);
      actions.setPositionLoader(false);
    });
    const responseData = response.data?.data ?? [];

    actions.setRecruiters(responseData);
    actions.setSelectedRecruiters([...responseData?.map((e) => e.id)]);
    return response;
  }),

  fetchBusinessAddress: thunk(async (actions, _, { getStoreState }) => {
    actions.setIsFetchingBusinessAddress(true);
    const controller = new MonitorByRecruitersController(getStoreState().http.client as HttpClientWithRetry);
    return controller
      .fetchBusinessAddress()
      .then((res) => {
        actions.setBusinessAddress(res.data.result.provinceCities);
        actions.setDistricts(res.data.result.districts);
        return res.data;
      })
      .finally(() => {
        actions.setIsFetchingBusinessAddress(false);
      });
  }),

  fetchPositions: thunk(async (actions, { accountId, fromApplyFilter }, { getState, getStoreState }) => {
    actions.setIsFetchingPosition(true);
    const { computedPositionFilters, positionFilters, isBulkAssignMode } = getState();
    let { page } = positionFilters;

    /* istanbul ignore next */
    if (fromApplyFilter) {
      page = 1;
      actions.setPositionFilter({ key: 'page', value: page });
    }

    const controller = new MonitorByRecruitersController(getStoreState().http.client as HttpClientWithRetry);
    return controller
      .fetchPosition(accountId, { ...computedPositionFilters, page })
      .then((res) => {
        actions.setPositions(res.data.data);
        actions.setPositionResponse(res.data);

        // istanbul ignore next
        if (isBulkAssignMode && fromApplyFilter) {
          actions.resetSelectedPositions();
        }
        return res;
      })
      .finally(() => {
        actions.setIsFetchingPosition(false);
      });
  }),

  fetchLocations: thunk(async (actions, _, { getState, getStoreState }) => {
    actions.setLocationFetching(true);
    const { locationQuery } = getState();
    const controller = new MonitorByRecruitersController(getStoreState().http.client as HttpClientWithRetry);
    return controller
      .fetchLocations(locationQuery)
      .then((res) => {
        actions.setLocations(res.data.data);
        return res;
      })
      .finally(() => {
        actions.setLocationFetching(false);
      });
  }),
  fetchJobs: thunk(async (_actions, { accountId }, { getStoreState }) => {
    const controller = new MonitorByRecruitersController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.fetchJobs(accountId);
    function sortObjectKeysDesc(
      obj: Record<string, 'open' | 'closed' | 'archived'>,
    ): Record<string, 'open' | 'closed' | 'archived'> {
      const sorted = Object.entries(obj).sort(/* istanbul ignore next */ ([a], [b]) => a.localeCompare(b));

      Object.keys(obj).forEach((k) => {
        delete obj[k];
      });

      sorted.forEach(([k, v]) => {
        obj[k] = v;
      });

      return obj;
    }
    return sortObjectKeysDesc(response.data.jobs);
  }),
  isFetchingBusinessAddress: false,
  updatePositionStatusOfRecruiter: thunk(async (actions, payload, { getState, getStoreState }) => {
    const { recruiterId, positionId, status } = payload;
    actions.setStatusUpdatingRecruiterId(recruiterId);

    try {
      const controller = new MonitorByRecruitersController(getStoreState().http.client as HttpClientWithRetry);
      const res = await controller.updatePositionStatusOfRecruiter(payload);

      const { positions, selectedPosition, recruiters } = getState();

      // Find target position (prefer selectedPosition if matches)
      const targetPosition = selectedPosition?.id === positionId ? selectedPosition : positions.find((p) => p.id === positionId);

      if (!targetPosition) {
        console.warn('Position not found:', positionId);
        return res;
      }

      // ---- Update Recruiter Stats ----
      const targetRecruiter = recruiters?.find((r) => r.id === recruiterId);
      if (targetRecruiter) {
        const isAssigned = targetPosition?.assignedRecruiters?.some((r) => r.id === recruiterId);
        const isWatched = targetPosition?.watchedRecruiters?.some((r) => r.id === recruiterId);

        let assignedPositionCount = targetRecruiter?.assignedPositionCount ?? 0;
        let watchedPositionCount = targetRecruiter?.watchedPositionCount ?? 0;

        if (status === FollowStateEnum.Followed) {
          assignedPositionCount += isAssigned ? 0 : 1;
          watchedPositionCount -= isWatched ? 1 : 0;
        } else if (status === FollowStateEnum.Watched) {
          watchedPositionCount += isWatched ? 0 : 1;
          assignedPositionCount -= isAssigned ? 1 : 0;
        }

        const updatedRecruiter = {
          ...targetRecruiter,
          assignedPositionCount,
          watchedPositionCount,
        };

        actions.setRecruiters(recruiters.map((r) => (r.id === recruiterId ? updatedRecruiter : r)));
      }

      // ---- Update Position ----
      const followedRecruiters =
        targetPosition?.assignedRecruiters?.filter((r) => r.id !== recruiterId) ?? /* istanbul ignore next */ null;
      const watchedRecruiters =
        targetPosition?.watchedRecruiters?.filter((r) => r.id !== recruiterId) ?? /* istanbul ignore next */ null;

      let assignedRecruitersCount = followedRecruiters?.length || 0;
      let watchedRecruitersCount = watchedRecruiters?.length || 0;

      if (status === FollowStateEnum.Followed) {
        followedRecruiters?.push({ id: recruiterId, name: '' });
        assignedRecruitersCount += 1;
      } else if (status === FollowStateEnum.Watched) {
        watchedRecruiters?.push({ id: recruiterId, name: '' });
        watchedRecruitersCount += 1;
      }

      const updatedPosition = {
        ...targetPosition,
        assignedRecruiters: followedRecruiters,
        watchedRecruiters,
        assignedRecruitersCount,
        watchedRecruitersCount,
      };

      actions.setPositions(positions.map((p) => (p.id === updatedPosition.id ? updatedPosition : p)));
      actions.setSelectedPosition(updatedPosition);

      return res;
    } finally {
      actions.setStatusUpdatingRecruiterId(null);
    }
  }),

  bulkUpdate: thunk(async (actions, payload, { getState, getStoreState }) => {
    actions.setBulkAssignInProgress(true);
    const { selectedPositions, selectedRecruiters, isAllPositionsSelected, computedPositionFilters } = getState();
    let newPayload: any = { ...payload, recruiterIds: selectedRecruiters };
    // istanbul ignore next
    if (isAllPositionsSelected) {
      newPayload = { ...newPayload, ...computedPositionFilters };
    } else {
      newPayload = { ...newPayload, positionDetails: selectedPositions };
    }
    try {
      const controller = new MonitorByRecruitersController(getStoreState().http.client as HttpClientWithRetry);
      return await controller.bulkUpdate(newPayload);
    } finally {
      actions.setBulkAssignInProgress(false);
    }
  }),
};
